import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type AssetCustomFieldValue } from '@air/api/types';

import {
  CustomFieldPlainText,
  type CustomFieldPlainTextProps,
} from '~/components/CustomFields/CustomFieldInput/CustomFieldPlainText';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdateItemsCustomFieldValue } from '~/utils/CustomFields/useUpdateItemsCustomFieldValue';

export type PrivateAssetCustomFieldPlainTextProps = {
  field: AssetCustomFieldValue;
  trackingLocation: AirActionTrackingLocation;
  canEditCustomFields: boolean;
  assetId: string;
} & Omit<CustomFieldPlainTextProps, 'onSubmit'>;

export const PrivateAssetCustomFieldPlainText = memo(
  ({ field, trackingLocation, canEditCustomFields, assetId, ...rest }: PrivateAssetCustomFieldPlainTextProps) => {
    const { updateItemsCustomFieldValue } = useUpdateItemsCustomFieldValue();
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceId = currentWorkspace?.id;

    const onSubmit = useCallback(
      async (value: string) => {
        if (!workspaceId) {
          return;
        }

        await updateItemsCustomFieldValue({
          assetIds: [assetId],
          baseCustomField: field,
          nextPlainTextValue: value,
          trackingLocation,
          workspaceId,
        });
      },
      [updateItemsCustomFieldValue, assetId, field, trackingLocation, workspaceId],
    );

    return <CustomFieldPlainText {...rest} onSubmit={canEditCustomFields ? onSubmit : undefined} field={field} />;
  },
);

PrivateAssetCustomFieldPlainText.displayName = 'PrivateAssetCustomFieldPlainText';
